import { isChineseEnvironment } from "@coworker/reusable/helpers/devices";
import { getEnvironmentId } from "@coworker/reusable/helpers/environment";
import type { ServiceName } from "../types";

const services: Record<
  ServiceName,
  { port: string; path: string; envKey: string }
> = {
  tasks: { port: "8081", path: "tasks-service", envKey: "TASKS_SERVICE" },
  testbuy: { port: "8082", path: "testbuy-service", envKey: "TESTBUY_SERVICE" },
  shoppingtools: {
    port: "8083",
    path: "shoppingtools-service",
    envKey: "SHOPPINGTOOLS_SERVICE",
  },
  mfaq: { port: "8084", path: "mfaq-service", envKey: "MFAQ_SERVICE" },
  core: { port: "8080", path: "core-service", envKey: "CORE_SERVICE" },
  roomsettings: {
    port: "8086",
    path: "roomsettings-service",
    envKey: "ROOMSETTINGS_SERVICE",
  },
  data: { port: "8087", path: "data-service", envKey: "DATA_SERVICE" },
  notifications: {
    port: "8088",
    path: "notifications-service",
    envKey: "NOTIFICATIONS_SERVICE",
  },
  //port is only here for semantics, you won't be able to run this locally through this port. You will have to start the project and see what port it uses. 443 is a random number
  cwh: { port: "443", path: "preferences", envKey: "CWH_SERVICE" },
};

export const ALL_SERVICES = Object.keys(services) as ServiceName[];

const LOCAL_OVERRIDES: Record<string, boolean> = {
  TASKS_SERVICE: !!import.meta.env["VITE_APP_LOCAL_TASKS_SERVICE"],
  TESTBUY_SERVICE: !!import.meta.env["VITE_APP_LOCAL_TESTBUY_SERVICE"],
  SHOPPINGTOOLS_SERVICE: !!import.meta.env[
    "VITE_APP_LOCAL_SHOPPINGTOOLS_SERVICE"
  ],
  MFAQ_SERVICE: !!import.meta.env["VITE_APP_LOCAL_MFAQ_SERVICE"],
  CORE_SERVICE: !!import.meta.env["VITE_APP_LOCAL_CORE_SERVICE"],
  ROOMSETTINGS_SERVICE: !!import.meta.env[
    "VITE_APP_LOCAL_ROOMSETTINGS_SERVICE"
  ],
  DATA_SERVICE: !!import.meta.env["VITE_APP_LOCAL_DATA_SERVICE"],
  NOTIFICATIONS_SERVICE: !!import.meta.env[
    "VITE_APP_LOCAL_NOTIFICATIONS_SERVICE"
  ],
  CWH_SERVICE: !!import.meta.env["VITE_APP_LOCAL_CWH_SERVICE"],
};

function resolveCwhBaseUrl(environmentId: string, path: string): string {
  const isProd =
    environmentId === "ingka-pmp-fixa-prod" ||
    environmentId === "ikea-coworker-app-prod";
  const cwhHost = isProd
    ? "https://api.ingka.ikea.com"
    : "https://api.ingka.cte.ikeadt.com";
  return `${cwhHost}/${path}`;
}

function baseUrl(
  environmentId: string,
  service: { port: string; path: string; envKey: string },
  isChina: boolean
): string {
  const { port, path, envKey } = service;

  const isLocal = LOCAL_OVERRIDES[envKey] ?? false;
  if (isLocal) return "http://localhost:" + port;

  if (envKey === "CWH_SERVICE") {
    return resolveCwhBaseUrl(environmentId, path);
  }

  if (isChina) {
    switch (environmentId) {
      case "prod":
        return `https://fixa-api.ingka-internal.cn/cn-s1/${path}`;
      case "dev":
      default:
        return `https://fixa-api-dev.ingka-dt.cn/cn-s1/${path}`;
    }
  }

  switch (environmentId) {
    case "ikea-coworker-app-stage":
    case "ingka-pmp-fixa-stage":
      return `https://api.stage.fixa.ingka.com/eu-w1/${path}`;
    case "ikea-coworker-app-prod":
    case "ingka-pmp-fixa-prod":
      return `https://api.fixa.ingka.com/eu-w1/${path}`;
    case "ingka-fixa-as-prod":
      return `https://api.fixa.ingka.com/as-e1/${path}`;
    case "ingka-fixa-us-prod":
      return `https://api.fixa.ingka.com/us-c1/${path}`;
    case "ikea-coworker-app-test":
    case "ikea-coworker-app-dev":
    case "ingka-pmp-fixa-dev":
    default:
      return `https://api.dev.fixa.ingka.com/eu-w1/${path}`;
  }
}

const isChina = isChineseEnvironment();
const environmentId = getEnvironmentId();

const resolvedUrls = Object.fromEntries(
  Object.entries(services).map(([name, config]) => [
    name,
    baseUrl(environmentId, config, isChina),
  ])
) as Record<ServiceName, string>;

export function resolveBaseUrl(service: ServiceName): string {
  return resolvedUrls[service]!;
}
